import { createExtensionService } from '@tencent/ark-extensions';
import docsComponentSizeLimitDialogConfig from './dc-components/docs-component-size-limit-dialog/component-config';
import attachmentComponentConfig from '@src/js/common/ark-extensions/dc-components/attachment-uploader/component-config';
import colorPickerComponentConfig from '@src/js/common/ark-extensions/dc-components/color-picker/component-config';
import { HELP_CENTER_EXTENSION } from '@src/js/common/ark-extensions/dc-components/help-center/component-config';
import guideInterFlow from '@src/js/common/ark-extensions/dc-components/guide-interflow/component-config';
import pcDocsComponentCollaborationConfig from '@src/js/common/ark-extensions/dc-components/docs-component-collaboration/pc/component-config';
import mobileDocsComponentCollaborationConfig from '@src/js/common/ark-extensions/dc-components/docs-component-collaboration/mobile/component-config';
import exportImageConfig from '@src/js/common/ark-extensions/dc-components/docs-component-export-image/component-config';
import ocrPureTextConfig from '@src/js/common/ark-extensions/dc-components/ocr-pure-text/component-config';
import transferAuthConfig from '@src/js/common/ark-extensions/dc-components/transfer-auth/component-config';
import exportAsyncConfig from '@src/js/common/ark-extensions/dc-components/export-async/component-config';
import vipToastConfig from '@src/js/common/ark-extensions/dc-components/vip-toast/component-config';
import vipToastMobileConfig from '@src/js/common/ark-extensions/dc-components/vip-toast-mobile/component-config';
import notificationsConfig from '@src/js/common/ark-extensions/dc-components/notifications/component-config';
import exportForAppGuidePanelConfig from '@src/js/common/ark-extensions/dc-components/export-for-app-guide-panel/component-config';
import permitApplyConfig from '@src/js/common/ark-extensions/dc-components/docs-component-permit-apply/component-config';
import wxQQH5LaunchConfig from '@src/js/common/ark-extensions/dc-components/docs-component-wx-h5-launch/component-config';
import docsComponentRecordConfig from '@src/js/common/ark-extensions/dc-components/docs-component-record/component-config';
import aiMobileGuideConfig from '@src/js/common/ark-extensions/dc-components/docs-component-ai-mobile-guide/component-config';
import aiEntranceConfig from '@src/js/common/ark-extensions/dc-components/docs-component-ai-entrance/component-config';
import type { ConnectionController } from '@tencent/docs-connection';
import { EXPORT_PREFERENCE_DIALOG_EXTENSION } from './dc-components/export-preference-dialog';
import { PAY_EXTENSION } from './dc-components/pay';
import { IMPORT, IMPORT_FILE, IMPORT_MULTI_FILE } from '@src/js/common/ark-extensions/dc-components/import-file/component-config';
import { ADV_PERMISSION_EXTENSION } from '@src/js/common/ark-extensions/dc-components/menu-adv-permission/config';
import { COOPERATION_PERMISSION_EXTENSION } from '@src/js/common/ark-extensions/dc-components/cooperation-permission/config';
import wcjsVideoPlayerConfig from '@src/js/common/ark-extensions/dc-components/wcjs-video-player/component-config';
import {
    DRIVE_SELECTOR_MOBILE, DRIVE_SELECTOR_PC,
} from '@src/js/common/ark-extensions/dc-components/docs-component-drive-selector/component-config';
import downloadConfig from '@src/js/common/ark-extensions/dc-components/docs-component-download/component-config';
import fileOperationConfig from '@src/js/common/ark-extensions/dc-components/docs-component-file-operation/component-config';
import { PDF_TO_OFFICE } from './dc-components/pdf-to-office/component-config';
import { TemplateMachineAuditConfig } from './dc-components/template-machine-audit/component-config';
import groupCreatorConfig from '@src/js/common/ark-extensions/dc-components/group-creator/component-config';
import vipLinktext from '@src/js/common/ark-extensions/dc-components/vip-linktext/component-config';
import modalToPay from '@src/js/common/ark-extensions/dc-components/modal-to-pay/component-config';
import sendToPCConfig from '@src/js/common/ark-extensions/dc-components/send-to-pc/component-config';
import mobileScreenshotConfig from '@src/js/common/ark-extensions/dc-components/mobile-screenshot/component-config';
import dclReportConfig from '@src/js/common/ark-extensions/dc-components/dcl-report/component-config';
import commentEditableConfig from '@src/js/common/ark-extensions/dc-components/docs-component-comment-editable/component-config';
import renameDialogConfig from '@src/js/common/ark-extensions/dc-components/rename-dialog/component-config';
import toolkitDialogConfig from '@src/js/common/ark-extensions/dc-components/toolkit-dialog/component-config';


const extensionService = createExtensionService({
    metadataLoaderOptions: {
        specifiedVersion: 1,
    },
});

const commonInit = (connectionController: ConnectionController) => {
    connectionController && extensionService.dependentServiceCombinder.connectionControllerService
        .setConnectionController(connectionController as any);
    /**
     * 手动初始化领域服务 https://iwiki.woa.com/pages/viewpage.action?pageId=1902350186
     * 在 basicClientVars 存在的时候，init 直接返回 true，ark 内通过 basicClientVars 获取数据
     * basicClientVars 不存在的时候，init 后通过 userInfo 接口获取数据，可以避免数据缺失
     */
    // 更新domainService
    if (window.clientVars) {
        extensionService.updateDomainWithClientVars(window.clientVars);
    }
};

/**
 * DC组件初始化 引用模块
 * （1）在./dc-components创建对应dc组件文件夹，component-config.ts为组件配置
 * （2）在当前文件中引入组件配置（./dc-components/xx/component-config.ts），后续页面ready后统一注册
 * （3）通过当前文件导出的ArkLoader.extensionService可加载组件，例如：ArkLoader.extensionService.getExtension(xxx)
 * （4）各组件的调用封装建议统一放在各自配置所在的文件夹./dc-components/xx/index.ts
 * 参考文档：https://doc.weixin.qq.com/doc/w3_m_aaItvQKFBYzG?scode=AJEAIQdfAAozOy4HXQAI4ApwYLACo
 */

// 暂存 initPromise 的 resolve 函数，用于使 initPromiseResolve 状态转为 fulfilled 状态
let initPromiseResolve: (value?: unknown) => void;
const ArkLoader = {
    // 在 initPromise.then 中 load dc 组件可以保证一定是在 init 之后执行
    initPromise: new Promise(resolve => initPromiseResolve = resolve),
    // ark服务实例（用以注册、获取组件）
    extensionService,
    // 初始化
    init: async (connectionController: ConnectionController): Promise<void> => {
        commonInit(connectionController);
        // 注册dc组件
        await extensionService.scanAndHandleExtensions([
            docsComponentSizeLimitDialogConfig,
            attachmentComponentConfig,
            HELP_CENTER_EXTENSION,
            PAY_EXTENSION,
            EXPORT_PREFERENCE_DIALOG_EXTENSION,
            guideInterFlow,
            pcDocsComponentCollaborationConfig,
            mobileDocsComponentCollaborationConfig,
            exportImageConfig,
            transferAuthConfig,
            exportAsyncConfig,
            ocrPureTextConfig,
            vipToastConfig,
            vipToastMobileConfig,
            notificationsConfig,
            exportForAppGuidePanelConfig,
            permitApplyConfig,
            wxQQH5LaunchConfig,
            docsComponentRecordConfig,
            aiMobileGuideConfig,
            aiEntranceConfig,
            IMPORT,
            IMPORT_FILE,
            IMPORT_MULTI_FILE,
            ADV_PERMISSION_EXTENSION,
            wcjsVideoPlayerConfig,
            COOPERATION_PERMISSION_EXTENSION,
            DRIVE_SELECTOR_MOBILE,
            DRIVE_SELECTOR_PC,
            downloadConfig,
            fileOperationConfig,
            PDF_TO_OFFICE,
            groupCreatorConfig,
            vipLinktext,
            modalToPay,
            colorPickerComponentConfig,
            sendToPCConfig,
            // mobileScreenshotConfig,
            dclReportConfig,
            commentEditableConfig,
            renameDialogConfig,
            TemplateMachineAuditConfig,
            toolkitDialogConfig,
        ]);
        // 在 init 之后修改 initPromise 的状态
        initPromiseResolve();
    },
    meetingRoomPlayerInit: async (connectionController: ConnectionController): Promise<void> => {
        commonInit(connectionController);
        // 注册dc组件
        await extensionService.scanAndHandleExtensions([
            attachmentComponentConfig,
        ]);
        // 在 init 之后修改 initPromise 的状态
        initPromiseResolve();
    },
    setConnectionController: (connectionController: ConnectionController): void => {
        extensionService.dependentServiceCombinder.connectionControllerService
            .setConnectionController(connectionController as any);
    },
};

export default ArkLoader;
